import { useContext, useState } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import {
  HandshakeOutlined,
  ShoppingCartOutlined,
  FactCheckOutlined,
  PrecisionManufacturingOutlined,
  CampaignOutlined,
  AssignmentTurnedInOutlined,
  ExtensionOutlined,
  CurrencyExchangeOutlined,
  AutoAwesomeOutlined
} from '@mui/icons-material';
import IOSSwitch from '../Switch/IOSSwitch';
import PluginsContext from '../../context/PluginsContext';
import MenuContext from '../../context/MenuContext';
import { useCurrencies } from '../../context/CurrencyContext';
import { updatePlugin } from '../../services/PluginService';
import SnackbarUtils from '../../utils/SnackbarUtils';

// Metadata solo visual (ícono + bajada) por plugin. El backend es la fuente
// de verdad para key/label/enabled — esto es puramente presentacional, así
// que un plugin nuevo sin entrada acá cae en el ícono/descripción genéricos
// en vez de romper el render.
const PLUGIN_PRESENTATION = {
  b2b: { icon: HandshakeOutlined, description: 'Clientes empresariales, perfiles comerciales, proformas, pedidos y estados de cuenta.' },
  ecommerce: { icon: ShoppingCartOutlined, description: 'Catálogo, checkout y pedidos de tu tienda online.' },
  b2b_portal: { icon: HandshakeOutlined, description: 'Acceso web para que tus clientes B2B consulten su cuenta y hagan pedidos.' },
  stock_count: { icon: FactCheckOutlined, description: 'Conteo cíclico de stock por sucursal, con cola de aprobación de ajustes.' },
  food_operations: { icon: PrecisionManufacturingOutlined, description: 'Insumos y preparaciones para operar recetas y costos de Food Service.' },
  production: { icon: PrecisionManufacturingOutlined, description: 'Elaboración, envasado, lotes y recursos de planta. Requiere Insumos y preparaciones.' },
  marketing: { icon: CampaignOutlined, description: 'Campañas de email, promociones y fidelización de clientes.' },
  staff_tasks: { icon: AssignmentTurnedInOutlined, description: 'Tareas y checklists asignadas al personal de cada sucursal.' },
  ai_assistant: { icon: AutoAwesomeOutlined, description: 'Un chat para preguntarle a tus datos (ventas, stock, costos). Usa tu propia clave de un proveedor de IA y envía los resultados de las consultas a ese proveedor. Al habilitarlo aparece Asistente en el menú.' },
  multi_currency: { icon: CurrencyExchangeOutlined, description: 'Precios, costos, ventas y reportes en más de una moneda. Al habilitarlo aparece Configuración > Monedas.' }
};
const DEFAULT_PRESENTATION = { icon: ExtensionOutlined, description: '' };
const MATURITY_PRESENTATION = {
  ESSENTIAL: { label: 'Esencial', color: 'primary' },
  OPTIONAL: { label: 'Opcional', color: 'default' },
  EXPERIMENTAL: { label: 'Experimental', color: 'warning' }
};

export default function PluginsSettingsTab({ readOnly = false }) {
  const { plugins, isPluginsLoaded, fetchPlugins } = useContext(PluginsContext);
  const { fetchMenu } = useContext(MenuContext);
  const { refresh: refreshCurrencies } = useCurrencies();
  const [togglingKey, setTogglingKey] = useState(null);
  // `available` ya se calcula en el backend según el tipo de empresa. Los
  // plugins de otra vertical no se muestran: no son configurables ni útiles
  // para esta empresa y solo agregan ruido visual.
  const availablePlugins = plugins.filter((plugin) => plugin.available);
  const labelFor = (key) => plugins.find((plugin) => plugin.key === key)?.label || key;

  const handleToggle = async (plugin) => {
    if (readOnly || togglingKey) return;

    setTogglingKey(plugin.key);
    try {
      await updatePlugin(plugin.key, !plugin.enabled);
      // El menú lateral se arma en el backend filtrando por plugin (MenuPluginGates) --
      // sin este refetch queda desactualizado hasta el próximo reload.
      await Promise.all([fetchPlugins(), fetchMenu(), refreshCurrencies()]);
    } catch (error) {
      console.error('Error updating plugin', error);
      SnackbarUtils.error('No se pudo actualizar el plugin');
    } finally {
      setTogglingKey(null);
    }
  };

  return (
    <>
      {!isPluginsLoaded && <Typography color="text.secondary">Cargando plugins...</Typography>}

      {isPluginsLoaded && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'start'
          }}
        >
          {availablePlugins.map((plugin) => {
            const { icon: Icon, description } = PLUGIN_PRESENTATION[plugin.key] || DEFAULT_PRESENTATION;
            const maturity = MATURITY_PRESENTATION[plugin.maturity] || MATURITY_PRESENTATION.OPTIONAL;
            const dependencyMessage = plugin.blockedBy?.length
              ? `Requiere activar: ${plugin.blockedBy.map(labelFor).join(', ')}.`
              : plugin.requiredBy?.length
                ? `Necesario para: ${plugin.requiredBy.map(labelFor).join(', ')}.`
                : null;
            return (
              <Paper
                key={plugin.key}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: plugin.enabled ? 'divider' : 'action.disabledBackground',
                  bgcolor: plugin.enabled ? 'background.paper' : 'action.hover'
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
                  <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                    <Icon sx={{ fontSize: 24, color: plugin.enabled ? 'text.primary' : 'text.disabled', flexShrink: 0 }} />
                    <Typography fontWeight={600} noWrap>{plugin.label}</Typography>
                  </Box>
                  <IOSSwitch
                    checked={plugin.enabled}
                    disabled={readOnly || togglingKey === plugin.key || plugin.canToggle === false}
                    onChange={() => handleToggle(plugin)}
                    inputProps={{ 'aria-label': `Activar ${plugin.label}` }}
                  />
                </Box>
                <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip size="small" label={maturity.label} color={maturity.color} variant="outlined" />
                </Box>
                {description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {description}
                  </Typography>
                )}
                {dependencyMessage && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {dependencyMessage}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </>
  );
}
